// =============================================================================
// DESIGN STUDIO - Agent IA pour création de visuels
// =============================================================================
// Création de templates devis, visuels marketing, documents
// Génération d'images IA via Gemini Nano Banana
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Palette,
  Image,
  FileText,
  Layout,
  Sparkles,
  Download,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Copy,
  Check,
  Eye,
  Wand2,
  PenTool,
  Type,
  Square,
  Circle,
  Layers,
  Settings,
  Upload,
  Save,
  ChevronRight,
  Maximize2,
  Grid3X3,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import DesignStudioEmailTemplates from "./widgets/DesignStudioEmailTemplates";
import { toast } from "sonner";
import DevisTemplateEditor from "./DevisTemplateEditor";
import { getCurrentTenantSlug } from "../../lib/utils";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Types de templates disponibles
const TEMPLATE_TYPES = [
  { id: "devis", label: "Devis", icon: FileText, color: "text-blue-400" },
  { id: "facture", label: "Facture", icon: FileText, color: "text-green-400" },
  { id: "bon_commande", label: "Bon de commande", icon: FileText, color: "text-orange-400" },
  { id: "pv_reception", label: "PV de réception", icon: FileText, color: "text-violet-400" },
  { id: "banniere", label: "Bannière web", icon: Layout, color: "text-pink-400" },
  { id: "post_social", label: "Post réseaux", icon: Square, color: "text-cyan-400" },
  { id: "flyer", label: "Flyer/Brochure", icon: Grid3X3, color: "text-amber-400" },
  { id: "email", label: "Template email", icon: Layout, color: "text-red-400" },
];

// Tailles d'images prédéfinies
const IMAGE_SIZES = [
  { id: "1024x1024", label: "Carré (1024x1024)", aspect: "1:1" },
  { id: "1536x1024", label: "Paysage (1536x1024)", aspect: "3:2" },
  { id: "1024x1536", label: "Portrait (1024x1536)", aspect: "2:3" },
  { id: "1200x630", label: "OG Image (1200x630)", aspect: "~2:1" },
  { id: "1080x1080", label: "Instagram (1080x1080)", aspect: "1:1" },
  { id: "1200x628", label: "LinkedIn (1200x628)", aspect: "~2:1" },
];

export default function DesignStudio({ tenantId }) {
  const [activeTab, setActiveTab] = useState("templates");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    type: "devis",
    nom: "",
    description: "",
    couleur_primaire: "#1e40af",
    couleur_secondaire: "#64748b",
    logo_position: "top-left",
    contenu: {}
  });
  
  // Génération d'images IA
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageSize, setImageSize] = useState("1024x1024");
  const [imageStyle, setImageStyle] = useState("professional");
  const [generatedImages, setGeneratedImages] = useState([]);
  
  // Bibliothèque d'assets
  const [assets, setAssets] = useState([]);

  // Charger les templates
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { ...enteteTenant(tenantId || getCurrentTenantSlug()) };
      const res = await fetch(`${API}/api/design/templates`, { headers });
      if (res.ok) {
        setTemplates(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement templates:", error);
      // Templates de démo
      setTemplates([
        {
          id: "1",
          type: "devis",
          nom: "Devis Standard",
          description: "Template de devis par défaut",
          couleur_primaire: "#1e40af",
          preview_url: null,
          actif: true
        },
        {
          id: "2",
          type: "banniere",
          nom: "Bannière été 2026",
          description: "Bannière promotionnelle été",
          couleur_primaire: "var(--oh-stat-orange-text)",
          preview_url: null,
          actif: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Charger les assets
  const loadAssets = useCallback(async () => {
    try {
      const headers = { ...enteteTenant(tenantId || getCurrentTenantSlug()) };
      const res = await fetch(`${API}/api/design/assets`, { headers });
      if (res.ok) {
        setAssets(await res.json());
      }
    } catch (error) {
      console.error("Erreur chargement assets:", error);
    }
  }, [tenantId]);

  useEffect(() => {
    loadTemplates();
    loadAssets();
  }, [loadTemplates, loadAssets]);

  // Générer une image IA
  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error("Veuillez décrire l'image à générer");
      return;
    }
    
    setGenerating(true);
    try {
      const headers = { 
        ...enteteTenant(tenantId || getCurrentTenantSlug()),
        "Content-Type": "application/json"
      };
      
      const res = await fetch(`${API}/api/design/generate-image`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          prompt: imagePrompt,
          size: imageSize,
          style: imageStyle
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedImages(prev => [data, ...prev]);
        toast.success("Image générée avec succès !");
      } else {
        let errorMsg = "Erreur lors de la génération";
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          // Body already read or not JSON
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Erreur génération:", error);
      toast.error("Erreur lors de la génération de l'image");
    } finally {
      setGenerating(false);
    }
  };

  // Sauvegarder un template
  const saveTemplate = async () => {
    try {
      const headers = { 
        ...enteteTenant(tenantId || getCurrentTenantSlug()),
        "Content-Type": "application/json"
      };
      
      const method = templateForm.id ? "PUT" : "POST";
      const url = templateForm.id 
        ? `${API}/api/design/templates/${templateForm.id}`
        : `${API}/api/design/templates`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(templateForm)
      });
      
      if (res.ok) {
        toast.success(templateForm.id ? "Template mis à jour" : "Template créé");
        setShowTemplateDialog(false);
        loadTemplates();
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      // Mode démo
      toast.success("Template sauvegardé (mode démo)");
      setShowTemplateDialog(false);
    }
  };

  // Supprimer un template
  const deleteTemplate = async (templateId) => {
    if (!confirm("Supprimer ce template ?")) return;
    
    try {
      const headers = { ...enteteTenant(tenantId || getCurrentTenantSlug()) };
      await fetch(`${API}/api/design/templates/${templateId}`, {
        method: "DELETE",
        headers
      });
      toast.success("Template supprimé");
      loadTemplates();
    } catch (error) {
      toast.success("Template supprimé (mode démo)");
      setTemplates(prev => prev.filter(t => t.id !== templateId));
    }
  };

  // Ouvrir le dialog de création
  const openNewTemplate = (type = "devis") => {
    setTemplateForm({
      type,
      nom: "",
      description: "",
      couleur_primaire: "#1e40af",
      couleur_secondaire: "#64748b",
      logo_position: "top-left",
      contenu: {}
    });
    setShowTemplateDialog(true);
  };

  // Obtenir l'icône du type
  const getTypeIcon = (type) => {
    const t = TEMPLATE_TYPES.find(tt => tt.id === type);
    return t?.icon || FileText;
  };

  const getTypeColor = (type) => {
    const t = TEMPLATE_TYPES.find(tt => tt.id === type);
    return t?.color || "text-slate-400";
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Palette className="w-8 h-8 text-pink-400" />
            Design Studio
          </h1>
          <p className="text-slate-400 mt-1">
            Création de templates, visuels marketing et documents
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadTemplates}
            disabled={loading}
            className="border-slate-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates Documents
          </TabsTrigger>
          <TabsTrigger value="images" className="flex items-center gap-2">
            <Wand2 className="w-4 h-4" />
            Génération Images IA
          </TabsTrigger>
          <TabsTrigger value="devis-pdf" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Template Devis PDF
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-2" data-testid="ds-tab-emails">
            <Mail className="w-4 h-4" />
            Emails Métier
          </TabsTrigger>
          <TabsTrigger value="assets" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Bibliothèque Assets
          </TabsTrigger>
        </TabsList>

        {/* === TEMPLATES DOCUMENTS === */}
        <TabsContent value="templates" className="space-y-6">
          {/* Types de templates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TEMPLATE_TYPES.map((type) => {
              const Icon = type.icon;
              const count = templates.filter(t => t.type === type.id).length;
              return (
                <Card 
                  key={type.id}
                  className="bg-slate-800/50 border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                  onClick={() => openNewTemplate(type.id)}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-slate-700/50`}>
                      <Icon className={`w-5 h-5 ${type.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">{type.label}</p>
                      <p className="text-zinc-400 text-xs">{count} template(s)</p>
                    </div>
                    <Plus className="w-4 h-4 text-zinc-400" />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Liste des templates */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Layout className="w-5 h-5 text-violet-400" />
                Mes Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
                  <p className="text-slate-400">Aucun template créé</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Cliquez sur un type ci-dessus pour créer votre premier template
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => {
                    const Icon = getTypeIcon(template.type);
                    return (
                      <div 
                        key={template.id}
                        className="bg-slate-700/30 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-colors"
                      >
                        {/* Preview */}
                        <div 
                          className="aspect-video bg-slate-800 rounded-lg mb-3 flex items-center justify-center border border-slate-600"
                          style={{ borderTopColor: template.couleur_primaire, borderTopWidth: "3px" }}
                        >
                          {template.preview_url ? (
                            <img 
                              src={template.preview_url} 
                              alt={template.nom}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Icon className={`w-12 h-12 ${getTypeColor(template.type)} opacity-50`} />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-white font-medium">{template.nom}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="bg-slate-600 text-xs">
                                {TEMPLATE_TYPES.find(t => t.id === template.type)?.label || template.type}
                              </Badge>
                              <div 
                                className="w-4 h-4 rounded-full border border-slate-600"
                                style={{ backgroundColor: template.couleur_primaire }}
                              />
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setTemplateForm(template);
                                setShowTemplateDialog(true);
                              }}
                              className="h-8 w-8 p-0 text-violet-400 hover:text-violet-300"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteTemplate(template.id)}
                              className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === GÉNÉRATION IMAGES IA === */}
        <TabsContent value="images" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-pink-400" />
                Générateur d'images IA
                <Badge className="bg-pink-500/20 text-pink-400 ml-2">Gemini Nano Banana</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Prompt */}
              <div>
                <Label className="text-slate-300">Description de l'image</Label>
                <Textarea
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  placeholder="Ex: Une bannière professionnelle pour une entreprise de pompes à chaleur, avec des tons bleus et verts, montrant une maison moderne écologique..."
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  rows={4}
                />
              </div>
              
              {/* Options */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Taille</Label>
                  <Select value={imageSize} onValueChange={setImageSize}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {IMAGE_SIZES.map((size) => (
                        <SelectItem key={size.id} value={size.id}>
                          {size.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Style</Label>
                  <Select value={imageStyle} onValueChange={setImageStyle}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professionnel</SelectItem>
                      <SelectItem value="modern">Moderne</SelectItem>
                      <SelectItem value="minimalist">Minimaliste</SelectItem>
                      <SelectItem value="creative">Créatif</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-end">
                  <Button
                    onClick={generateImage}
                    disabled={generating || !imagePrompt.trim()}
                    className="w-full bg-pink-600 hover:bg-pink-700"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Générer
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Images générées */}
              {generatedImages.length > 0 && (
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">Images générées ({generatedImages.length})</h4>
                    {generatedImages.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => setGeneratedImages([])}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Tout effacer
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {generatedImages.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={img.url} 
                          alt={img.prompt}
                          className="w-full aspect-square object-cover rounded-md border border-slate-700"
                          style={{ maxHeight: '120px' }}
                        />
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-white hover:bg-zinc-900/50/20"
                            onClick={() => window.open(img.url, '_blank')}
                          >
                            <Maximize2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-white hover:bg-zinc-900/50/20"
                            onClick={() => {
                              const a = document.createElement('a');
                              a.href = img.url;
                              a.download = `generated_${idx}.png`;
                              a.click();
                            }}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-400 hover:bg-red-500/20"
                            onClick={() => {
                              setGeneratedImages(prev => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === TEMPLATE DEVIS PDF === */}
        <TabsContent value="devis-pdf" className="space-y-6">
          <DevisTemplateEditor tenantId={tenantId} />
        </TabsContent>

        {/* === EMAILS MÉTIER (JJ 2026-05-20) === */}
        <TabsContent value="emails" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-400" />
                Emails Métier
              </CardTitle>
              <p className="text-xs text-slate-400">
                Invitations client/installateur — gérés ici depuis Design
                Studio. Les emails Système (alertes Gamora, AG-04) restent
                en Gouvernance → Configuration → Emails &amp; Notifications.
              </p>
            </CardHeader>
            <CardContent>
              <DesignStudioEmailTemplates />
            </CardContent>
          </Card>
        </TabsContent>

        {/* === BIBLIOTHÈQUE ASSETS === */}
        <TabsContent value="assets" className="space-y-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-400" />
                Bibliothèque d'Assets
              </CardTitle>
              <Button className="bg-cyan-600 hover:bg-cyan-700">
                <Upload className="w-4 h-4 mr-2" />
                Importer
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <Image className="w-16 h-16 mx-auto text-zinc-400 mb-4" />
                <p className="text-slate-400">Bibliothèque d'assets vide</p>
                <p className="text-zinc-400 text-sm mt-2">
                  Importez vos logos, icônes et images pour les utiliser dans vos templates
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Template */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              {templateForm.id ? "Modifier le template" : "Nouveau template"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Type</Label>
              <Select
                value={templateForm.type}
                onValueChange={(value) => setTemplateForm(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-300">Nom du template</Label>
              <Input
                value={templateForm.nom}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: Devis Premium"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description du template..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Couleur primaire</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={templateForm.couleur_primaire}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, couleur_primaire: e.target.value }))}
                    className="w-12 h-10 p-1 bg-slate-800 border-slate-700"
                  />
                  <Input
                    value={templateForm.couleur_primaire}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, couleur_primaire: e.target.value }))}
                    className="flex-1 bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Couleur secondaire</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="color"
                    value={templateForm.couleur_secondaire}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, couleur_secondaire: e.target.value }))}
                    className="w-12 h-10 p-1 bg-slate-800 border-slate-700"
                  />
                  <Input
                    value={templateForm.couleur_secondaire}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, couleur_secondaire: e.target.value }))}
                    className="flex-1 bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTemplateDialog(false)}
              className="border-slate-600"
            >
              Annuler
            </Button>
            <Button
              onClick={saveTemplate}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
